import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { PaginationBar } from "components/Pagination";
import Button from "components/Button/Regular";
import Input from "components/Input/Input";
import {
  AppointmentComplete,
  createAppointment,
  deleteAppointment,
  readOrgAppointments,
  updateAppointment,
} from "services/appointments.service";
import { APPOINTMENT_TYPE } from "constants/appointmentEnums";
import vgToast from "utils/toast";

type Props = { orgId: number };

type FormState = {
  appointmentTitle: string;
  start: string;
  end: string;
};

const EMPTY_FORM: FormState = {
  appointmentTitle: "",
  start: "",
  end: "",
};

const Appointments = ({ orgId }: Props) => {
  const { getAccessTokenSilently } = useAuth0();
  const [appointments, setAppointments] = React.useState<AppointmentComplete[]>([]);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [showCreate, setShowCreate] = React.useState(false);
  const [form, setForm] = React.useState<FormState>(EMPTY_FORM);

  const load = React.useCallback(async () => {
    if (orgId <= 0) return;
    setLoading(true);
    setLoadError(null);
    try {
      const token = await getAccessTokenSilently();
      const { data, error } = await readOrgAppointments(token, orgId);
      if (error) {
        setLoadError(error.message);
        return;
      }
      setAppointments(data ?? []);
    } catch (e) {
      setLoadError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [orgId, getAccessTokenSilently]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!form.appointmentTitle.trim()) {
      vgToast.error("Appointment title is required.");
      return;
    }
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const { error } = await createAppointment(token, {
        orgId,
        appointmentTitle: form.appointmentTitle,
        appointmentType: APPOINTMENT_TYPE.CALENDAR,
        start: form.start || undefined,
        end: form.end || undefined,
      });
      if (error) {
        vgToast.error(error.message);
        return;
      }
      vgToast.success("Appointment added.");
      setShowCreate(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (appointmentId: number) => {
    if (!form.appointmentTitle.trim()) {
      vgToast.error("Appointment title is required.");
      return;
    }
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const { error } = await updateAppointment(token, {
        orgId,
        appointmentId,
        appointmentTitle: form.appointmentTitle,
        appointmentType: APPOINTMENT_TYPE.CALENDAR,
        start: form.start || undefined,
        end: form.end || undefined,
      });
      if (error) {
        vgToast.error(error.message);
        return;
      }
      vgToast.success("Appointment saved.");
      setEditingId(null);
      setForm(EMPTY_FORM);
      await load();
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (appointmentId: number) => {
    if (!window.confirm("Delete this appointment?")) return;
    try {
      const token = await getAccessTokenSilently();
      const { error } = await deleteAppointment(token, orgId, appointmentId);
      if (error) {
        vgToast.error(error.message);
        return;
      }
      vgToast.success("Appointment removed.");
      await load();
    } catch (e) {
      vgToast.error((e as Error).message);
    }
  };

  const startEdit = (appt: AppointmentComplete) => {
    setShowCreate(false);
    setEditingId(appt.appointmentId);
    setForm({
      appointmentTitle: appt.appointmentTitle,
      start: appt.start ?? "",
      end: appt.end ?? "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setShowCreate(false);
    setForm(EMPTY_FORM);
  };

  return (
    <div>
      <div className="overflow-x-auto bg-white border border-slate-200 rounded-[10px]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70 border-b border-slate-200">
            <tr className="text-left text-[0.7rem] font-semibold uppercase tracking-[0.05em] text-slate-500">
              <th className="px-5 py-3">Title</th>
              <th className="px-5 py-3">Start</th>
              <th className="px-5 py-3">End</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="text-slate-700">
            {appointments.map((appt) =>
              editingId === appt.appointmentId ? (
                <tr key={appt.appointmentId} className="border-b border-slate-100 last:border-b-0 bg-slate-50/60">
                  <td className="px-5 py-4" colSpan={4}>
                    <div className="flex flex-col gap-y-2">
                      <Input
                        label="Title"
                        value={form.appointmentTitle}
                        onChange={(e) => setForm((f) => ({ ...f, appointmentTitle: e.target.value }))}
                      />
                      <div className="flex flex-col gap-4 sm:flex-row sm:gap-x-4">
                        <Input
                          type="datetime-local"
                          label="Start"
                          value={form.start}
                          onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
                        />
                        <Input
                          type="datetime-local"
                          label="End"
                          value={form.end}
                          onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button color="Primary" onClick={() => handleUpdate(appt.appointmentId)} disabled={submitting}>
                          <span>{submitting ? "Saving…" : "Save"}</span>
                        </Button>
                        <Button color="Secondary" onClick={cancelEdit} disabled={submitting}>
                          <span>Cancel</span>
                        </Button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={appt.appointmentId} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60 transition-colors">
                  <td className="px-5 py-3 align-middle">{appt.appointmentTitle}</td>
                  <td className="px-5 py-3 align-middle">{appt.start ?? "—"}</td>
                  <td className="px-5 py-3 align-middle">{appt.end ?? "—"}</td>
                  <td className="px-5 py-3 align-middle whitespace-nowrap">
                    <div className="flex gap-1.5 justify-end">
                      <button
                        type="button"
                        className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-vg-blue/10 text-vg-blue hover:bg-vg-blue/15 transition-colors"
                        onClick={() => startEdit(appt)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-vg-cherry/10 text-vg-cherry hover:bg-vg-cherry/15 transition-colors"
                        onClick={() => handleDelete(appt.appointmentId)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              )
            )}
            {appointments.length === 0 && !loading && (
              <tr>
                <td colSpan={4} className="px-5 py-8 text-center text-sm text-slate-400">
                  No appointments.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {loading && <div className="mt-3 text-sm text-slate-500">Loading appointments...</div>}
      {loadError && <div className="mt-3 text-sm text-red-500">{loadError}</div>}

      {showCreate && (
        <div className="mt-4 p-5 bg-white border border-slate-200 rounded-[10px] flex flex-col gap-3">
          <Input
            label="Title"
            value={form.appointmentTitle}
            onChange={(e) => setForm((f) => ({ ...f, appointmentTitle: e.target.value }))}
          />
          <div className="flex flex-col gap-4 sm:flex-row sm:gap-x-4">
            <Input
              type="datetime-local"
              label="Start"
              value={form.start}
              onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
            />
            <Input
              type="datetime-local"
              label="End"
              value={form.end}
              onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button color="Primary" onClick={handleCreate} disabled={submitting}>
              <span>{submitting ? "Creating…" : "Create"}</span>
            </Button>
            <Button color="Secondary" onClick={cancelEdit} disabled={submitting}>
              <span>Cancel</span>
            </Button>
          </div>
        </div>
      )}

      {!showCreate && editingId === null && (
        <div className="mt-3">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-vg-blue/10 text-vg-blue hover:bg-vg-blue/15 transition-colors"
            onClick={() => setShowCreate(true)}
          >
            + Add Appointment
          </button>
        </div>
      )}

      <div className="mt-4">
        <PaginationBar from={1} to={appointments.length} total={appointments.length} />
      </div>
    </div>
  );
};

export default Appointments;
